/* eslint-disable import/no-cycle */
/**
 * Copyright 2014-present CloudIO Inc. All Rights Reserved.
 */

import { atom, useStateXAtom } from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { WebSocketContext } from './useWebSocket';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    textAlign: 'center',
    visibility: 'hidden',
  },
  main: {
    visibility: 'visible',
    backgroundColor: '#f9edbe',
    border: '1px solid #f0c36d',
    borderTop: 0,
    borderRadius: '0 0 2px 2px',
    boxShadow: `0 2px 4px ${theme.palette.divider}`,
    color: '#222',
    padding: '6px 10px',
    display: 'inline-block',
    fontSize: '0.9em',
  },
  bold: {
    fontWeight: 'bold',
    marginRight: 8,
  },
  try: {
    marginLeft: 8,
    backgroundColor: 'transparent',
    border: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));

export const readyStateAtom = atom({
  path: ['ws', 'readyState'],
  defaultValue: -1,
});

const OnlineIndicator = memo(() => {
  const [readyState, setReadyState] = useStateXAtom(readyStateAtom);
  const [retries, setRetries] = useState(0);
  const [retryInSeconds, setRetryInSeconds] = useState(0);
  const [retryTime, setRetryTime] = useState(new Date().getTime() + 6000000);
  const classes = useStyles();
  const { doConnect } = useContext(WebSocketContext);

  useEffect(() => {
    if (readyState === -9) {
      // server restarting
      const retryTimeVal = new Date().getTime() + 300000;
      setRetryTime(retryTimeVal);
      setRetryInSeconds(
        Math.round((retryTimeVal - new Date().getTime()) / 1000),
      );
      setReadyState(3);
      setRetries(1);
    } else if (readyState > 1) {
      const timer = setInterval(() => {
        setRetryInSeconds(
          Math.round((retryTime - new Date().getTime()) / 1000),
        );
      }, 500);
      return () => clearInterval(timer);
    }
  }, [retryTime, readyState, setReadyState]);

  const retryFn = useCallback(() => {
    setRetries(retries + 1);
    doConnect();
    const retryTimeVal = new Date().getTime() + retries * 20000;
    setRetryTime(retryTimeVal);
    setRetryInSeconds(Math.round((retryTimeVal - new Date().getTime()) / 1000));
  }, [retries, doConnect]);

  useEffect(() => {
    if (readyState === 3 && retries === 0) {
      retryFn();
    } else if (readyState === 1) {
      setRetries(0);
    }
  }, [readyState, retries, retryFn]);

  useEffect(() => {
    if (retryInSeconds <= 0 && readyState === 3) {
      retryFn();
    }
  }, [retryInSeconds, readyState, retryFn]);

  if (readyState === 1) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.main}>
        <span className={classes.bold}>
          {readyState === 0 ? 'Connecting...' : 'Not connected.'}
        </span>
        {readyState !== 0 ? (
          <>
            Connecting in <span>{retryInSeconds}</span>s
            <button className={classes.try} type="button" onClick={retryFn}>
              Try now
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
});

OnlineIndicator.displayName = 'OnlineIndicator';

export default OnlineIndicator;
